import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useSlotRecipe} from '@chakra-ui/react3'
import PriceProduct from '../price-product'
import CTA from '../cta'
import priceButtonGroupRecipe from './index.recipe'
import {hasValidPrice, hasValidString} from '../../../utils/utils'

/**
 * PriceButtonGroup component displays a price label alongside a CTA button.
 * Used when showing product pricing with a single action button.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {Object} props.price - Price configuration object.
 * @param {number} props.price.value - The numeric price value.
 * @param {string} [props.price.currency] - Currency code (e.g., 'USD', 'EUR').
 * @param {string} [props.price.label] - Optional label to display before price.
 * @param {Object} [props.cta] - CTA button configuration.
 * @param {string} props.cta.label - Button label text.
 * @param {Object} props.cta.action - Button action configuration.
 * @param {'large'|'small'} props.size- Button size variant
 * @param {Object} trackingContext - Context for tracking purposes.
 * @param {string} trackingContext.moduleName - Module name for tracking.
 * @param {string} variant - Background and price color variant.
 * @returns {JSX.Element} Rendered price and button group.
 */
const PriceButtonGroup = ({price, cta, size = 'large', trackingContext, variant = 'default'}) => {
    const hasPrice = hasValidPrice(price?.value)
    const hasCtaLabel = hasValidString(cta?.label)

    const recipe = useSlotRecipe({recipe: priceButtonGroupRecipe})
    const styles = useMemo(
        () => recipe({size, hasPrice, variant}),
        [recipe, size, hasPrice, variant]
    )

    if (!hasPrice && !hasCtaLabel) return null

    return (
        <Box css={styles.root}>
            {hasPrice && (
                <Text css={styles.price}>
                    <span>{price?.label ? `${price?.label} ` : ''}</span>
                    <PriceProduct {...price} />
                </Text>
            )}
            {hasCtaLabel && <CTA {...cta} {...styles.cta} trackingContext={trackingContext} />}
        </Box>
    )
}

PriceButtonGroup.propTypes = {
    price: PropTypes.shape({
        currency: PropTypes.string,
        value: PropTypes.number,
        label: PropTypes.string
    }),
    cta: PropTypes.shape({
        label: PropTypes.string,
        action: PropTypes.shape({
            type: PropTypes.oneOf(['category', 'content', 'product', 'url', 'anchor']),
            value: PropTypes.string
        })
    }),
    ctaVariant: PropTypes.oneOf(['primary', 'secondary']),
    size: PropTypes.oneOf(['small', 'large']),
    trackingContext: PropTypes.shape({
        moduleName: PropTypes.string
    }),
    variant: PropTypes.oneOf(['default', 'lifestyle', 'product'])
}

export default PriceButtonGroup
